import type { CustomQuery, MetricKey } from '@contracts';

/**
 * Equivalente CUSTOM de una métrica de CATÁLOGO — para "Personalizar" (forkear) un
 * widget oficial y VER/EDITAR su modelo, filtros y reglas desde la UI.
 *
 * Solo se mapean las que se expresan limpio como `modelo Odoo + cálculo + filtros (+ regla)`.
 * NO se pueden mapear (y por eso no muestran "personalizable"):
 *   - SmartBamboo (sources 'sb') → vienen de Metabase; el builder es solo-Odoo.
 *   - Logística → vienen de pos_rest_api, fuera del allowlist gobernado.
 *   - Derivadas/complejas: Ticket promedio (razón), Heatmap (2D), Productos (desglose de
 *     líneas), Vendedores/Sucursales (atribución por comisión) → no son un solo modelo+filtro.
 */
export const CATALOG_AS_CUSTOM: Partial<Record<MetricKey, CustomQuery>> = {
  // Total Ventas: suma de órdenes con la regla maestra de ventas netas.
  sales: {
    model: 'pos.order',
    aggregation: 'sum',
    field: 'amount_total',
    domain: [],
    masterRules: ['netSales'],
    visualization: 'kpi',
    dateField: 'date_order',
  },
  // Transacciones: nº de órdenes netas (sin devueltas/refunds, por la regla).
  orders: {
    model: 'pos.order',
    aggregation: 'count',
    domain: [],
    masterRules: ['netSales'],
    visualization: 'kpi',
    dateField: 'date_order',
  },
  // Devoluciones: órdenes con total negativo (los refunds).
  returnsCount: {
    model: 'pos.order',
    aggregation: 'count',
    domain: [{ field: 'amount_total', op: '<', value: 0 }],
    masterRules: [],
    visualization: 'kpi',
    dateField: 'date_order',
  },
  // Tendencia diaria: ventas netas agrupadas por día (serie).
  dailyTrend: {
    model: 'pos.order',
    aggregation: 'sum',
    field: 'amount_total',
    domain: [],
    masterRules: ['netSales'],
    groupBy: 'date_order',
    groupByInterval: 'day',
    visualization: 'area',
    dateField: 'date_order',
  },
  // Mix de pagos: monto por método de pago (ranking). Custom no hace dona → barra.
  paymentMix: {
    model: 'pos.payment',
    aggregation: 'sum',
    field: 'amount',
    domain: [],
    groupBy: 'payment_method_id',
    groupLimit: 20,
    masterRules: [],
    visualization: 'bar',
    dateField: 'payment_date',
  },
  // Clientes Registrados: nº de clientes (customer_rank>0) creados en el período.
  newClients: {
    model: 'res.partner',
    aggregation: 'count',
    domain: [{ field: 'customer_rank', op: '>', value: 0 }],
    masterRules: [],
    visualization: 'kpi',
    dateField: 'create_date',
  },
  // Apartados (abonado): suma de lo abonado en apartados ACTIVOS (filtro de estado visible).
  layaways: {
    model: 'pos.wishlist',
    aggregation: 'sum',
    field: 'amount_paid',
    domain: [{ field: 'state', op: '=', value: 'active' }],
    masterRules: [],
    visualization: 'kpi',
    dateField: 'date_created',
  },
};

export const hasCustomPreset = (metric: MetricKey | null): boolean =>
  !!metric && !!CATALOG_AS_CUSTOM[metric];
